import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import EditIcon from "@mui/icons-material/Edit";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
	getLlmNoQueueSignatures,
	LlmNoQueueSignatures,
	QueueSignature,
} from "../../api/grade_connect_queue/llm-no-signatures";
import { CopyIconButton } from "../shared/paper-section";

const DEALBREAKER_LABELS: Record<string, string> = {
	overqualified: "Overqualified",
	current_role_irrelevant: "Current role irrelevant",
	offered_role_irrelevant: "Offered role irrelevant",
	out_of_league: "Out of league",
	unwilling_for_required_skills: "Unwilling for required skills",
};

// Synthetic id for the auto-injected egenkonsult / freelancer mandatory.
// Mirrors EGENKONSULT_VIRTUAL_ID in core/endpoints/requirement_blockers.py.
const EGENKONSULT_VIRTUAL_ID = -1;
const EGENKONSULT_LABEL = "Freelancer";
const EGENKONSULT_DESCRIPTION =
	"Candidate must currently work as an independent consultant in their own company — own LLC, sole trader, or solo / one-person business. Employed candidates and those at multi-person firms fail this check. Auto-added when the assignment's Freelancer toggle is on.";

const DEALBREAKER_DESCRIPTIONS: Record<string, string> = {
	overqualified:
		"Too senior for the role's target — C-level/VP/Director, 2+ levels above, or experience ~2× the minimum.",
	current_role_irrelevant:
		"Most recent role doesn't use the mandatory stack — different function/domain, or stack unused in the last ~24 months.",
	offered_role_irrelevant:
		"Offered role is a major pivot vs the candidate's recent trajectory — different function/domain or seniority step-down.",
	out_of_league:
		"Too prestigious/senior to realistically be attracted to this role — top-tier C-level, well-known public profile, or comp mismatch.",
	unwilling_for_required_skills:
		"Candidate explicitly stated in CV/profile that they want to move away from the required tech.",
};

/** Query param that opens the rejected queue with every chip unchecked once
 * the bar knows the role's requirement ids. */
export const UNCHECK_ALL_PARAM = "rejected";

/** The candidates the LLM passed for a role and that wait for the recruiter's
 * vote: the list the Todo card's Vote button and "Review n waiting" open. */
export const waitingCandidatesUrl = (assignmentId: number): string =>
	`/grading/${assignmentId}?method=llm`;

/** The rejected candidates of a role, all of them, not filtered by any reason. */
export const reviewAllRejectedUrl = (assignmentId: number): string =>
	`/grading/${assignmentId}?method=llm_no&${UNCHECK_ALL_PARAM}=all`;

/** The rejected candidates whose one unmet must-have is `requirementId`:
 * that chip unchecked (`req_off`), every risk flag left checked (`risk_off`). */
export const reviewRejectedOnRequirementUrl = (
	assignmentId: number,
	requirementId: number
): string =>
	`/grading/${assignmentId}?method=llm_no&req_off=${requirementId}&risk_off=`;

/** Where "Artemis' analysis" sends "Review rejected": the people rejected on
 * the must-have in question alone, or the whole list when it names none. */
export const diagnosisReviewUrl = (
	assignmentId: number,
	reviewRequirementId: number | null
): string =>
	reviewRequirementId === null
		? reviewAllRejectedUrl(assignmentId)
		: reviewRejectedOnRequirementUrl(assignmentId, reviewRequirementId);

const allUnchecked = (
	data: LlmNoQueueSignatures
): { unblockedRequirementIds: number[]; unblockedRiskFlags: string[] } => ({
	unblockedRequirementIds: data.mandatory_requirements.map((r) => r.id),
	unblockedRiskFlags: [...data.risk_flags],
});

export interface LlmNoFilterBarProps {
	assignmentId: number;
	unblockedRequirementIds: number[];
	unblockedRiskFlags: string[];
	onChange: (next: {
		unblockedRequirementIds: number[];
		unblockedRiskFlags: string[];
	}) => void;
	onEditRequirementsClick: () => void;
	/** Bumped by the parent after the assignment-edit modal saves so the bar refetches. */
	refetchKey?: number;
	/** Rendered under an empty LLM-yes queue: reviewing the rejected is the
	 * main way forward, so it leads and the reason chips fold away. */
	emptyQueue?: boolean;
	/** The role's AI analysis above already offers both actions, so the bar
	 * only keeps the folded rejection reasons. */
	actionsAbove?: boolean;
	/** Uncheck every chip as soon as the chip data has loaded. */
	uncheckAllOnLoad?: boolean;
}

const visibleCount = (
	signatures: QueueSignature[],
	offReqs: Set<number>,
	offFlags: Set<string>
): number => {
	let total = 0;
	for (const sig of signatures) {
		const reqOk = sig.unmet_req_ids.every((id) => offReqs.has(id));
		const flagOk = sig.risk_flags.every((f) => offFlags.has(f));
		if (reqOk && flagOk) total += sig.count;
	}
	return total;
};

/** Signed change in `visibleCount` if this chip were toggled.
 * ON chip → off: returns +N (gain). OFF chip → on: returns -N (loss). */
const toggleDeltaForReq = (
	signatures: QueueSignature[],
	offReqs: Set<number>,
	offFlags: Set<string>,
	reqId: number
): number => {
	const base = visibleCount(signatures, offReqs, offFlags);
	const next = new Set(offReqs);
	if (next.has(reqId)) next.delete(reqId);
	else next.add(reqId);
	return visibleCount(signatures, next, offFlags) - base;
};

const toggleDeltaForFlag = (
	signatures: QueueSignature[],
	offReqs: Set<number>,
	offFlags: Set<string>,
	flag: string
): number => {
	const base = visibleCount(signatures, offReqs, offFlags);
	const next = new Set(offFlags);
	if (next.has(flag)) next.delete(flag);
	else next.add(flag);
	return visibleCount(signatures, offReqs, next) - base;
};

/** "+N" / "-N" / "+0" — always signed so direction is unambiguous. */
const formatDelta = (n: number): string => (n >= 0 ? `+${n}` : `${n}`);

const HELP_TEXT =
	"This bar shows what the AI rejected. Each filter chip is a single rejection reason and the +N is how many extra candidates would qualify if you accepted that reason. The takeaway: every requirement and dealbreaker costs you candidates. Remove or soften the ones that aren't truly mandatory and you'll get more results — and usually higher quality, because the AI can spend its budget on the requirements that actually matter to you.";

const LlmNoFilterBar = ({
	assignmentId,
	unblockedRequirementIds,
	unblockedRiskFlags,
	onChange,
	onEditRequirementsClick,
	refetchKey,
	emptyQueue = false,
	actionsAbove = false,
	uncheckAllOnLoad = false,
}: LlmNoFilterBarProps) => {
	const [reasonsOpen, setReasonsOpen] = useState(!emptyQueue);
	const [data, setData] = useState<LlmNoQueueSignatures | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		getLlmNoQueueSignatures(assignmentId)
			.then((d) => {
				if (cancelled) return;
				setData(d);
				if (uncheckAllOnLoad) onChange(allUnchecked(d));
			})
			.catch((err) => {
				if (!cancelled) {
					console.error("llm-no-queue-signatures failed:", err);
					setData(null);
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [assignmentId, refetchKey]);

	if (loading || !data) {
		return (
			<Box
				data-component="llm-no-filter-bar-loading"
				sx={{ display: "flex", justifyContent: "center", py: 1 }}
			>
				<CircularProgress size={20} />
			</Box>
		);
	}

	if (data.mandatory_requirements.length === 0 && data.risk_flags.length === 0) {
		return null;
	}

	const offReqs = new Set(unblockedRequirementIds);
	const offFlags = new Set(unblockedRiskFlags);
	const visible = visibleCount(data.signatures, offReqs, offFlags);
	const totalCandidates = data.signatures.reduce((sum, sig) => sum + sig.count, 0);

	const buildLlmText = (): string => {
		const reqById = new Map(data.mandatory_requirements.map((r) => [r.id, r.text]));
		const reqRows = data.mandatory_requirements.map((req) => {
			const on = !offReqs.has(req.id);
			const delta = toggleDeltaForReq(data.signatures, offReqs, offFlags, req.id);
			return `| ${on ? "ON" : "OFF"} | ${req.text} | ${formatDelta(delta)} |`;
		});
		const flagRows = data.risk_flags.map((flag) => {
			const on = !offFlags.has(flag);
			const label = DEALBREAKER_LABELS[flag] ?? flag;
			const delta = toggleDeltaForFlag(data.signatures, offReqs, offFlags, flag);
			return `| ${on ? "ON" : "OFF"} | ${label} | ${formatDelta(delta)} |`;
		});
		const sigRows = data.signatures
			.slice()
			.sort((a, b) => b.count - a.count)
			.map((s) => {
				const reqs = s.unmet_req_ids
					.map((id) => reqById.get(id) ?? `req#${id}`)
					.join(", ");
				const flags = s.risk_flags
					.map((f) => DEALBREAKER_LABELS[f] ?? f)
					.join(", ");
				return `| ${reqs || "—"} | ${flags || "—"} | ${s.count} |`;
			});
		return [
			`# LLM_NO queue filter snapshot — assignment ${assignmentId}`,
			"",
			`Visible queue: ${visible} candidate${visible === 1 ? "" : "s"}`,
			"",
			`## Mandatory requirements (${data.mandatory_requirements.length})`,
			"",
			"| Status | Requirement | Toggle Δ |",
			"|---|---|---|",
			...reqRows,
			"",
			`## Dealbreakers (${data.risk_flags.length})`,
			"",
			"| Status | Dealbreaker | Toggle Δ |",
			"|---|---|---|",
			...flagRows,
			"",
			`## Signature histogram (${data.signatures.length} distinct, sorted by count desc)`,
			"",
			"| Unmet requirements | Dealbreakers | Count |",
			"|---|---|---|",
			...sigRows,
		].join("\n");
	};

	const toggleReq = (id: number) => {
		const next = new Set(offReqs);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		onChange({
			unblockedRequirementIds: Array.from(next).sort((a, b) => a - b),
			unblockedRiskFlags: Array.from(offFlags).sort(),
		});
	};

	const toggleFlag = (flag: string) => {
		const next = new Set(offFlags);
		if (next.has(flag)) next.delete(flag);
		else next.add(flag);
		onChange({
			unblockedRequirementIds: Array.from(offReqs).sort((a, b) => a - b),
			unblockedRiskFlags: Array.from(next).sort(),
		});
	};

	const anyFlagOn = data.risk_flags.some((f) => !offFlags.has(f));
	// Toggle direction: if any flag is ON, clicking turns ALL off (gain);
	// if all flags are OFF, clicking turns ALL on (loss). Mirrors toggleAllFlags.
	const toggleAllFlagsTarget = anyFlagOn
		? new Set(data.risk_flags)
		: new Set<string>();
	const allFlagsDelta =
		visibleCount(data.signatures, offReqs, toggleAllFlagsTarget) - visible;
	const toggleAllFlags = () => {
		const next = anyFlagOn ? new Set(data.risk_flags) : new Set<string>();
		onChange({
			unblockedRequirementIds: Array.from(offReqs).sort((a, b) => a - b),
			unblockedRiskFlags: Array.from(next).sort(),
		});
	};

	const anyChipOn =
		data.mandatory_requirements.some((r) => !offReqs.has(r.id)) || anyFlagOn;
	const showAllRejected = () => onChange(allUnchecked(data));

	return (
		<Stack
			data-component="llm-no-filter-bar"
			spacing={1.5}
			sx={{
				p: 2,
				mb: 2,
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
				bgcolor: "background.paper",
			}}
		>
			<Stack
				direction="row"
				spacing={1}
				alignItems="center"
				sx={{ flexWrap: "wrap", gap: 1 }}
			>
				<Typography
					variant="subtitle2"
					data-component="llm-no-filter-bar-headline"
				>
					{emptyQueue
						? `The AI rejected ${totalCandidates.toLocaleString("sv-SE")} candidates for this role`
						: "Filter the AI's rejection reasons — uncheck a chip to preview those candidates"}
				</Typography>
				<Tooltip title={HELP_TEXT} placement="bottom-start" arrow>
					<HelpOutlineIcon
						fontSize="small"
						color="action"
						data-component="llm-no-filter-bar-help"
					/>
				</Tooltip>
				<CopyIconButton getText={buildLlmText} />
				<Box sx={{ flex: 1 }} />
				{!emptyQueue && (
					<Typography
						variant="body2"
						color="text.secondary"
						data-component="llm-no-filter-bar-count"
					>
						{visible} / {totalCandidates} candidate
						{totalCandidates === 1 ? "" : "s"} match
					</Typography>
				)}
				{!actionsAbove && (
					<>
						<Button
							data-component="show-all-rejected-button"
							variant={emptyQueue ? "contained" : "outlined"}
							size={emptyQueue ? "medium" : "small"}
							disabled={!anyChipOn}
							onClick={showAllRejected}
						>
							{emptyQueue
								? `Review the ${totalCandidates.toLocaleString("sv-SE")} rejected`
								: `Show all rejected (${totalCandidates})`}
						</Button>
						<Button
							data-component="edit-requirements-button"
							variant={emptyQueue ? "outlined" : "contained"}
							size={emptyQueue ? "medium" : "small"}
							startIcon={<EditIcon />}
							onClick={onEditRequirementsClick}
						>
							Edit requirements
						</Button>
					</>
				)}
			</Stack>
			{emptyQueue && (
				<Box>
					<Button
						data-component="toggle-rejection-reasons"
						size="small"
						onClick={() => setReasonsOpen((open) => !open)}
					>
						{reasonsOpen
							? "Hide rejection reasons"
							: "Show rejection reasons"}
					</Button>
				</Box>
			)}
			<Collapse in={reasonsOpen} data-component="rejection-reasons">
				<Stack spacing={1.5}>
					<Typography
						variant="caption"
						color="text.secondary"
						data-component="llm-no-filter-bar-explanation"
					>
						Use these filters to see which requirements actually matter —
						high +N means the requirement is excluding many candidates. If
						those candidates still look relevant, the requirement may not
						belong as mandatory. Tighter, more relevant requirements yield
						more matches and higher quality.
					</Typography>

					{data.mandatory_requirements.length > 0 && (
						<Stack
							direction="row"
							spacing={1}
							alignItems="center"
							sx={{ flexWrap: "wrap", gap: 1 }}
							data-component="llm-no-filter-bar-requirements"
						>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ minWidth: 100 }}
							>
								Requirements
							</Typography>
							{data.mandatory_requirements.map((req) => {
								const on = !offReqs.has(req.id);
								const isEgenkonsult = req.id === EGENKONSULT_VIRTUAL_ID;
								const displayText = isEgenkonsult
									? EGENKONSULT_LABEL
									: req.text;
								const description = isEgenkonsult
									? EGENKONSULT_DESCRIPTION
									: "";
								const delta = toggleDeltaForReq(
									data.signatures,
									offReqs,
									offFlags,
									req.id
								);
								const chip = (
									<Chip
										key={req.id}
										data-component={
											isEgenkonsult
												? "filter-chip-freelancer"
												: "filter-chip-requirement"
										}
										icon={
											on ? (
												<CheckBoxIcon fontSize="small" />
											) : (
												<CheckBoxOutlineBlankIcon fontSize="small" />
											)
										}
										label={`${displayText} ${formatDelta(delta)}`}
										size="small"
										color={on ? "primary" : "default"}
										variant={on ? "filled" : "outlined"}
										onClick={() => toggleReq(req.id)}
									/>
								);
								return description ? (
									<Tooltip
										key={req.id}
										title={description}
										placement="top"
										arrow
									>
										{chip}
									</Tooltip>
								) : (
									chip
								);
							})}
						</Stack>
					)}

					{data.risk_flags.length > 0 && (
						<Stack
							direction="row"
							spacing={1}
							alignItems="center"
							sx={{ flexWrap: "wrap", gap: 1 }}
							data-component="llm-no-filter-bar-dealbreakers"
						>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ minWidth: 100 }}
							>
								Dealbreakers
							</Typography>
							<Chip
								data-component="filter-chip-all-dealbreakers"
								icon={
									anyFlagOn ? (
										<CheckBoxIcon fontSize="small" />
									) : (
										<CheckBoxOutlineBlankIcon fontSize="small" />
									)
								}
								label={`All dealbreakers ${formatDelta(allFlagsDelta)}`}
								size="small"
								color={anyFlagOn ? "warning" : "default"}
								variant={anyFlagOn ? "filled" : "outlined"}
								onClick={toggleAllFlags}
								sx={{ fontWeight: 600 }}
							/>
							{data.risk_flags.map((flag) => {
								const on = !offFlags.has(flag);
								const label = DEALBREAKER_LABELS[flag] ?? flag;
								const description =
									DEALBREAKER_DESCRIPTIONS[flag] ?? "";
								const delta = toggleDeltaForFlag(
									data.signatures,
									offReqs,
									offFlags,
									flag
								);
								const chip = (
									<Chip
										key={flag}
										data-component="filter-chip-dealbreaker"
										icon={
											on ? (
												<CheckBoxIcon fontSize="small" />
											) : (
												<CheckBoxOutlineBlankIcon fontSize="small" />
											)
										}
										label={`${label} ${formatDelta(delta)}`}
										size="small"
										color={on ? "warning" : "default"}
										variant={on ? "filled" : "outlined"}
										onClick={() => toggleFlag(flag)}
									/>
								);
								return description ? (
									<Tooltip
										key={flag}
										title={description}
										placement="top"
										arrow
									>
										{chip}
									</Tooltip>
								) : (
									chip
								);
							})}
						</Stack>
					)}
				</Stack>
			</Collapse>
		</Stack>
	);
};

export default LlmNoFilterBar;
